import { useEffect, useState } from 'react';
import type { INews } from '@skystone/shared';
import { NEWS_KIND_META } from '@skystone/shared';
import { newsApi } from '@/services/news-api';
import { Spinner } from '@/components/ui';
import { NEWS_KIND_ICON } from '@/lib/news-kind-icons';

function formatNewsDate(eventDate: string): string {
  const date = new Date(eventDate);
  const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const day = date.getDate();
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();
  return year === currentYear ? `${month} ${day}` : `${month} ${day} ${year}`;
}

export function WhatsNewFeed() {
  const [items, setItems] = useState<INews[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    newsApi
      .getAll({ limit: 6 })
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load announcements.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="space-y-4">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          What&apos;s new
        </p>
        <h2 className="text-xl font-bold tracking-tight text-foreground">
          Recent releases and announcements
        </h2>
      </div>

      {error && <p className="text-sm text-muted-foreground">{error}</p>}

      {!error && items === null && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading…
        </div>
      )}

      {!error && items !== null && items.length === 0 && (
        <p className="text-sm text-muted-foreground">No announcements yet.</p>
      )}

      {!error && items !== null && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((item) => {
            const Icon = NEWS_KIND_ICON[item.kind];
            const meta = NEWS_KIND_META[item.kind];
            return (
              <li key={item.id} className="flex gap-3">
                <Icon className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 space-y-1">
                  <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {meta.verb} {formatNewsDate(item.eventDate)}
                    {item.categoryTag ? ` · ${item.categoryTag}` : ''}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
